import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, Switch, Divider, TextField,
  Alert, Tabs, Tab, Stack, Avatar, alpha, IconButton, Chip, Button,
  FormControl, Select, MenuItem, Slider,
} from '@mui/material';
import {
  People, Settings, Psychology, Refresh, CheckCircle, Tune,
  Compare, GroupWork, School, Favorite,
} from '@mui/icons-material';
import { settingsAPI } from '../../api/client';
import { integerOnly } from '../../utils/inputHelpers';

function AdminRoomPairingPage() {
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [ps, setPs] = useState({
    enabled: 'true', algorithm: 'compatibility_score',
    min_compatibility: '60', max_age_gap: '5',
    weight_lifestyle: '30', weight_academic: '25',
    weight_cleanliness: '25', weight_social: '20',
    auto_suggest: 'true', allow_preferences: 'true',
    gender_match: 'true', faculty_preference: 'false',
  });

  useEffect(() => {
    settingsAPI.list({ category: 'room_pairing' }).then(res => {
      if (Array.isArray(res.data)) {
        const m = {}; res.data.forEach(s => { m[s.key] = s.value; });
        setPs(p => ({ ...p, ...m }));
      }
    }).catch(() => {});
  }, []);

  const saveSetting = async (key, value) => {
    try {
      await settingsAPI.update(key, { value: String(value) });
      setPs(p => ({ ...p, [key]: String(value) }));
      setSuccess('Setting updated');
    } catch (_) { setError('Failed to update'); }
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" fontWeight={800} sx={{ background: 'linear-gradient(135deg, #ec4899, #f472b6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Room Pairing
          </Typography>
          <Typography variant="body2" color="text.secondary">Matching algorithm, compatibility settings, and preferences</Typography>
        </Box>
        <IconButton sx={{ bgcolor: 'white', boxShadow: 1 }}><Refresh /></IconButton>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper sx={{ borderRadius: 3, mb: 3, px: 1 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ '& .MuiTabs-indicator': { height: 3, borderRadius: 2 } }}>
          <Tab label="Matching Settings" icon={<Psychology sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="Compatibility Weights" icon={<Tune sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="Preferences & Criteria" icon={<Favorite sx={{ fontSize: 18 }} />} iconPosition="start" />
        </Tabs>
      </Paper>

      {tabValue === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Matching Algorithm</Typography><Divider sx={{ mb: 2 }} />
              <Stack spacing={3}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box><Typography fontWeight={600}>Enable Room Pairing</Typography><Typography variant="body2" color="text.secondary">Allow roommate matching system</Typography></Box>
                  <Switch checked={ps.enabled==='true'} onChange={(e)=>saveSetting('enabled', e.target.checked?'true':'false')} />
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box><Typography fontWeight={600}>Auto-Suggest Matches</Typography><Typography variant="body2" color="text.secondary">Automatically suggest compatible roommates</Typography></Box>
                  <Switch checked={ps.auto_suggest==='true'} onChange={(e)=>saveSetting('auto_suggest', e.target.checked?'true':'false')} />
                </Stack>
                <Box><Typography fontWeight={600} gutterBottom>Matching Algorithm</Typography>
                  <FormControl size="small" fullWidth>
                    <Select value={ps.algorithm} onChange={(e) => saveSetting('algorithm', e.target.value)}>
                      <MenuItem value="compatibility_score">Compatibility Score</MenuItem>
                      <MenuItem value="preference_based">Preference Based</MenuItem>
                      <MenuItem value="hybrid">Hybrid (Score + Preferences)</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box><Typography fontWeight={600} gutterBottom>Min Compatibility Score (%)</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Minimum score to suggest a match</Typography>
                  <TextField size="small" type="number" value={ps.min_compatibility}
                    onChange={(e)=>setPs(p=>({...p,min_compatibility:e.target.value}))}
                    onKeyDown={integerOnly}
                    onBlur={(e)=>saveSetting('min_compatibility', e.target.value)} sx={{ width: 120 }} />
                </Box>
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Match Statistics</Typography><Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                {[
                  { label: 'Total Matches Made', value: '156', color: '#6366f1', icon: <GroupWork /> },
                  { label: 'Avg Compatibility', value: '78%', color: '#10b981', icon: <Compare /> },
                  { label: 'Active Pairings', value: '42', color: '#f59e0b', icon: <People /> },
                  { label: 'Success Rate', value: '92%', color: '#ec4899', icon: <Favorite /> },
                ].map((item, i) => (
                  <Paper key={i} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Avatar sx={{ bgcolor: alpha(item.color, 0.1), color: item.color, width: 36, height: 36 }}>{item.icon}</Avatar>
                        <Typography fontWeight={600}>{item.label}</Typography>
                      </Stack>
                      <Typography variant="h6" fontWeight={800} color={item.color}>{item.value}</Typography>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}

      {tabValue === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Compatibility Weights</Typography><Divider sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Adjust how much each factor contributes to the overall compatibility score. Total should equal 100%.</Typography>
              <Stack spacing={4}>
                {[
                  { key: 'weight_lifestyle', label: 'Lifestyle Habits', desc: 'Sleep schedule, noise, guests', icon: <People /> },
                  { key: 'weight_academic', label: 'Academic Schedule', desc: 'Study times, course load', icon: <School /> },
                  { key: 'weight_cleanliness', label: 'Cleanliness Standards', desc: 'Tidiness, cleaning frequency', icon: <CheckCircle /> },
                  { key: 'weight_social', label: 'Social Preferences', desc: 'Introvert/extrovert, visitors', icon: <Favorite /> },
                ].map(item => (
                  <Box key={item.key}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                      {item.icon}
                      <Box>
                        <Typography fontWeight={600}>{item.label}</Typography>
                        <Typography variant="caption" color="text.secondary">{item.desc}</Typography>
                      </Box>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Slider value={Number(ps[item.key])} min={0} max={100}
                        onChange={(e,v) => setPs(p=>({...p,[item.key]:String(v)}))}
                        onChangeCommitted={(e,v) => saveSetting(item.key, String(v))}
                        sx={{ flexGrow: 1 }} />
                      <Typography fontWeight={700} sx={{ minWidth: 40 }}>{ps[item.key]}%</Typography>
                    </Stack>
                  </Box>
                ))}
              </Stack>
              <Paper variant="outlined" sx={{ p: 2, mt: 3, borderRadius: 2 }}>
                <Typography fontWeight={600}>
                  Total: {Number(ps.weight_lifestyle) + Number(ps.weight_academic) + Number(ps.weight_cleanliness) + Number(ps.weight_social)}%
                </Typography>
                <Typography variant="caption" color={
                  Number(ps.weight_lifestyle) + Number(ps.weight_academic) + Number(ps.weight_cleanliness) + Number(ps.weight_social) === 100
                    ? 'success.main' : 'error.main'
                }>
                  {Number(ps.weight_lifestyle) + Number(ps.weight_academic) + Number(ps.weight_cleanliness) + Number(ps.weight_social) === 100
                    ? 'Weights are balanced' : 'Weights should total 100%'}
                </Typography>
              </Paper>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Questionnaire Preview</Typography><Divider sx={{ mb: 2 }} />
              <Stack spacing={1.5}>
                {[
                  'What time do you usually go to bed?',
                  'How often do you study in your room?',
                  'How would you rate your tidiness (1-5)?',
                  'Do you prefer a quiet or social environment?',
                  'How often do you have guests over?',
                  'Do you smoke or drink?',
                  'What is your preferred room temperature?',
                  'Are you an early bird or night owl?',
                ].map((q, i) => (
                  <Paper key={i} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Avatar sx={{ bgcolor: alpha('#ec4899', 0.1), color: '#ec4899', width: 24, height: 24, fontSize: '0.7rem' }}>{i+1}</Avatar>
                      <Typography variant="body2">{q}</Typography>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}

      {tabValue === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Matching Criteria</Typography><Divider sx={{ mb: 2 }} />
              <Stack spacing={3}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box><Typography fontWeight={600}>Gender Matching</Typography><Typography variant="body2" color="text.secondary">Only match same gender</Typography></Box>
                  <Switch checked={ps.gender_match==='true'} onChange={(e)=>saveSetting('gender_match', e.target.checked?'true':'false')} />
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box><Typography fontWeight={600}>Faculty Preference</Typography><Typography variant="body2" color="text.secondary">Prefer matching within same faculty</Typography></Box>
                  <Switch checked={ps.faculty_preference==='true'} onChange={(e)=>saveSetting('faculty_preference', e.target.checked?'true':'false')} />
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box><Typography fontWeight={600}>Allow User Preferences</Typography><Typography variant="body2" color="text.secondary">Users can set their own roommate criteria</Typography></Box>
                  <Switch checked={ps.allow_preferences==='true'} onChange={(e)=>saveSetting('allow_preferences', e.target.checked?'true':'false')} />
                </Stack>
                <Box><Typography fontWeight={600} gutterBottom>Max Age Gap (years)</Typography>
                  <TextField size="small" type="number" value={ps.max_age_gap}
                    onChange={(e)=>setPs(p=>({...p,max_age_gap:e.target.value}))}
                    onKeyDown={integerOnly}
                    onBlur={(e)=>saveSetting('max_age_gap', e.target.value)} sx={{ width: 120 }} />
                </Box>
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Deal Breakers</Typography><Divider sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Criteria that automatically disqualify a match</Typography>
              <Stack spacing={1.5}>
                {[
                  { rule: 'Different gender (when gender match enabled)', active: true },
                  { rule: 'Smoking preference mismatch', active: true },
                  { rule: 'Pet allergy conflicts', active: true },
                  { rule: 'Extreme sleep schedule difference', active: false },
                  { rule: 'Religious dietary conflicts', active: false },
                ].map((item, i) => (
                  <Paper key={i} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="body2">{item.rule}</Typography>
                      <Switch defaultChecked={item.active} />
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}

export default AdminRoomPairingPage;
